import { buildLineItemPath } from '@/features/activity/card-links-utils';
import { evidenceLine } from 'shared/src/wrkthru/evidence-line';
import type {
  OperatorRole,
  PortfolioStatusCounts,
  WrkthruIssue,
  WrkthruPagePayload,
  WrkthruTask,
  ZohoTaskStatus
} from 'shared/src/wrkthru/types';
import type { DailyBrief } from 'shared/src/trpc-types/daily-brief-trpc-types';

// Hardcoded Zoho org id, matching the copies in details-rail and io-summary.
const ZOHO_CRM_TASK_BASE = 'https://crm.zoho.com/crm/org643932192/tab/Tasks';

const KNOWN_TASK_STATUSES: ReadonlyArray<ZohoTaskStatus> = [
  'Not Started',
  'In Progress',
  'Waiting on someone else',
  'Deferred',
  'Completed'
];

// Zoho Status is a free-text picklist on the wire; coerce to the strict union the
// TaskCard styles against, defaulting unseen values to 'Not Started'.
function toTaskStatus(status: string): ZohoTaskStatus {
  return (KNOWN_TASK_STATUSES as ReadonlyArray<string>).includes(status)
    ? (status as ZohoTaskStatus)
    : 'Not Started';
}

/**
 * Widen the lean `dailyBrief` query result into the `WrkthruPagePayload` the
 * Wrkthru page renders. Issues arrive in final per-bucket order, each carrying
 * its bucket, rank, and status label, so the adapter just maps them through.
 * `today` is a `yyyy-MM-dd` string supplied by the caller because the brief is a
 * point-in-time read and carries no date of its own.
 */
export function dailyBriefToWrkthruPayload(brief: DailyBrief, today: string): WrkthruPagePayload {
  const role: OperatorRole = brief.operator.role;

  const issues = brief.issues.map(issue => toIssue(issue, today));
  const tasks = brief.tasks.tasks.map(task => toTask(task, brief.tasks.campaigns));

  return {
    date: today,
    operator: {
      id: brief.operator.id,
      name: brief.operator.name,
      role
    },
    assignment: {
      role,
      primaryCount: brief.assignment.primaryCount,
      secondaryCount: brief.assignment.secondaryCount
    },
    portfolioStatus: portfolioStatus(role, brief.assignment.primaryCount),
    issues,
    tasks
  };
}

// Counts stay zero: the page re-derives the status-bar buckets; primaryCount is the denominator.
function portfolioStatus(role: OperatorRole, primaryCount: number): PortfolioStatusCounts {
  return {
    unit: role === 'media-trader' ? 'line-item' : 'campaign',
    total: primaryCount,
    counts: { P1: 0, P2: 0, P3: 0, P4: 0, P5: 0 },
    unknown: primaryCount
  };
}

function toIssue(issue: DailyBrief['issues'][number], today: string): WrkthruIssue {
  return {
    id: issue.lineItemId,
    bucket: issue.bucket,
    rankInBucket: issue.rankInBucket,
    statusCode: issue.code,
    statusLabel: issue.statusLabel,
    scope: 'line-item',
    entityName: issue.lineItemName || issue.campaignName || issue.lineItemId,
    entityHref: buildLineItemPath(issue.campaignId, issue.lineItemId, 'execution'),
    description: evidenceLine(issue),
    ...(issue.narrative ? { whyNow: issue.narrative } : {}),
    detectedAt: `${today}T00:00:00Z`
  };
}

function toTask(
  task: DailyBrief['tasks']['tasks'][number],
  campaigns: DailyBrief['tasks']['campaigns']
): WrkthruTask {
  const campaignName = task.campaign_id
    ? (campaigns.find(c => c.campaign.campaign_id === task.campaign_id)?.campaign.campaign_name ??
      task.What_Id?.name ??
      'Related campaign')
    : (task.What_Id?.name ?? 'Unrelated task');

  return {
    id: task.id,
    title: task.Subject ?? 'Untitled task',
    status: toTaskStatus(task.Status),
    // bucket/taskType/rank/why come straight off the wire; the reconciler guarantees each Task carries them.
    taskType: task.taskType,
    relatedCampaignName: campaignName,
    relatedCampaignHref: task.campaign_id ? `/campaigns/${task.campaign_id}` : '',
    description: task.Description ?? '',
    whyThisPriority: task.whyThisPriority,
    zohoHref: `${ZOHO_CRM_TASK_BASE}/${task.id}`,
    bucket: task.bucket,
    rankInBucket: task.rankInBucket,
    dueDate: task.Due_Date ?? undefined
  };
}
